import * as Sentry from '@sentry/react';
import type {
  IPeerConnection,
  IPeerConnectionFactory,
  PeerConnectionOptions,
  TransceiverSpec,
} from '@playcastdotio/PeerCore';
import {
  SdpInspector,
  WebRtcOptimizationManager,
  configFromMetadata,
  IceHealthGuard,
  SdpGatherBuffer,
} from '@playcastdotio/PeerCore';
import type { PeerMetadata } from '@playcastdotio/Messaging';

const VIEWER_RECVONLY_TRANSCEIVERS: TransceiverSpec[] = [
  { kind: 'audio', direction: 'recvonly' },
  { kind: 'video', direction: 'recvonly' },
];

export type RetryPolicy = {
  maxRetries: number;
  shouldRetry: (reason: string, attempt: number) => boolean;
  retryDelayMs: (attempt: number) => number;
};

export type ClientPeerCallbacks = {
  onConnected: () => void;
  onDisconnected: (reason: string) => void;
  onRemoteStream: (stream: MediaStream) => void;
  onDataMessage: (data: string | ArrayBuffer) => void;
  onSignalGenerated: (sdp: RTCSessionDescriptionInit) => void;
  onIceCandidatesGenerated: (candidates: RTCIceCandidate[]) => void;
  onRetrying: (attempt: number, reason: string) => void;
  onIceChecking?: () => void;
  onIceConnected?: () => void;
  onIceFailed?: () => void;
};

/**
 * ClientPeerManager - Class-based, injectable WebRTC peer manager
 * for marketing-demo (player/client side).
 *
 * Uses a single impolite (polite=false) connection via IPeerConnectionFactory.
 * Supports configurable retry logic via RetryPolicy.
 * Fully mockable for testing without WebRTC infra.
 */
export class ClientPeerManager {
  private factory: IPeerConnectionFactory;
  private callbacks: ClientPeerCallbacks;
  private retryPolicy: RetryPolicy | undefined;
  private optimizationManager = new WebRtcOptimizationManager();
  private sdpInspector = new SdpInspector();

  private connection: IPeerConnection | null = null;
  private preWarmedConnection: IPeerConnection | null = null;
  private connected = false;
  private retryAttempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  private pendingCloseReason: string | null = null;
  private hostCloseInProgress = false;
  private closed = false;
  private connectionEpoch = 0;
  private disconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private lastHostPeerId: string | undefined;
  private lastIceServers: RTCIceServer[] | undefined;
  private lastMetadata: PeerMetadata | undefined;
  private gatherBuffer: SdpGatherBuffer | null = null;
  private preWarmCreatedAt = 0;
  private remoteDescriptionSet = false;
  private pendingRemoteIceCandidates: RTCIceCandidate[] = [];

  private static readonly ICE_GATHER_TIMEOUT_MS = 500;
  private static readonly PREWARM_MAX_AGE_MS = 60_000;
  private static readonly ICE_DISCONNECT_GRACE_MS = 5_000;

  constructor(
    factory: IPeerConnectionFactory,
    callbacks: ClientPeerCallbacks,
    retryPolicy?: RetryPolicy,
  ) {
    this.factory = factory;
    this.callbacks = callbacks;
    this.retryPolicy = retryPolicy;
  }

  /**
   * Pre-create an RTCPeerConnection with iceCandidatePoolSize so STUN/TURN
   * binding starts immediately, well before signaling begins.
   */
  public preWarm(iceServers: RTCIceServer[]): void {
    this.destroyPreWarmedConnection();
    this.preWarmedConnection = this.factory.create({
      config: {
        iceServers,
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
        iceCandidatePoolSize: 10,
      },
      batchCandidates: true,
      batchCandidatesTimeout: 1000,
      enableDataChannels: true,
      channelLabel: 'data',
    });
    this.preWarmCreatedAt = Date.now();
    // NOTE: Do NOT add transceivers here. Pre-warm runs while peer-lite is
    // still inactive; adding transceivers would fire a 'negotiationneeded'
    // event that peer-lite bails on, leaving [[NeedsNegotiation]] set and
    // preventing any future event from firing (see WebRTC spec step 5 of
    // "update the negotiation-needed flag"). Transceivers are added in
    // connect() instead, right before start() flips isActive=true.
    console.log('[ClientPeerManager] Pre-warmed RTCPeerConnection with iceCandidatePoolSize=10');
  }

  public hasPreWarmedConnection(): boolean {
    return this.preWarmedConnection !== null;
  }

  public connect(
    hostPeerId: string,
    iceServers: RTCIceServer[],
    metadata: PeerMetadata,
  ): void {
    this.lastHostPeerId = hostPeerId;
    this.lastIceServers = iceServers;
    this.lastMetadata = metadata;

    this.closed = false;
    this.cancelDisconnectTimer();
    this.destroyConnection();
    this.pendingCloseReason = null;
    this.connectionEpoch++;
    this.remoteDescriptionSet = false;
    this.pendingRemoteIceCandidates = [];

    const optimizationConfig = configFromMetadata(metadata);

    let connection: IPeerConnection;
    if (this.preWarmedConnection && this.isPreWarmHealthy()) {
      console.log('[ClientPeerManager] Using pre-warmed connection');
      connection = this.preWarmedConnection;
      this.preWarmedConnection = null;
    } else {
      if (this.preWarmedConnection) {
        console.warn('[ClientPeerManager] Discarding stale/unhealthy pre-warmed connection');
        this.destroyPreWarmedConnection();
      }
      const options: PeerConnectionOptions = {
        id: hostPeerId,
        config: {
          iceServers,
          bundlePolicy: 'max-bundle',
          rtcpMuxPolicy: 'require',
          iceCandidatePoolSize: 10,
        },
        batchCandidates: true,
        batchCandidatesTimeout: 1000,
        enableDataChannels: true,
        channelLabel: 'data',
      };
      connection = this.factory.create(options);
    }

    this.connection = connection;

    // Declare recvonly audio+video transceivers BEFORE start() so the first
    // offer already contains m=audio and m=video. This MUST happen here (and
    // not in preWarm) so that the 'negotiationneeded' event fires while
    // peer-lite is about to become active — any addTransceiver done while
    // isActive=false would be silently consumed by peer-lite's bail guard
    // and leave [[NeedsNegotiation]] set, preventing a future event.
    connection.ensureTransceivers(VIEWER_RECVONLY_TRANSCEIVERS);

    this.optimizationManager.applyPreConnectionOptimizations(
      connection.getRtcPeerConnection(),
      optimizationConfig,
    );

    this.wireEvents(connection, optimizationConfig);
    connection.start({ polite: false });
  }

  public async handleRemoteSdp(sdp: RTCSessionDescriptionInit): Promise<void> {
    if (!this.connection) {
      console.warn('[ClientPeerManager] No connection to signal SDP');
      return;
    }
    await this.connection.signal(sdp);
    this.remoteDescriptionSet = true;
    this.drainPendingRemoteIceCandidates();
    this.logRemoteCandidateCount(this.connection);
  }

  private logRemoteCandidateCount(connection: IPeerConnection): void {
    IceHealthGuard.getCandidateCounts(connection).then((counts) => {
      if (!counts) return;
      console.log(`[ClientPeerManager] Post-SDP stats: ${counts.remoteCandidates} remote candidates, ${counts.candidatePairs} candidate pairs`);
      Sentry.addBreadcrumb({
        category: 'webrtc',
        message: 'Post-setRemoteDescription candidate stats',
        level: counts.remoteCandidates === 0 ? 'warning' : 'info',
        data: counts,
      });
      if (counts.remoteCandidates === 0) {
        console.warn('[ClientPeerManager] No remote candidates after setRemoteDescription — ICE will fail');
      }
    });
  }

  public handleRemoteIceCandidate(candidate: RTCIceCandidate): Promise<void> {
    if (!this.connection) {
      console.warn('[ClientPeerManager] No connection to add ICE candidate');
      return Promise.resolve();
    }
    if (!this.remoteDescriptionSet) {
      this.pendingRemoteIceCandidates.push(candidate);
      return Promise.resolve();
    }
    return this.connection.addIceCandidate(candidate);
  }

  private drainPendingRemoteIceCandidates(): void {
    if (!this.connection || this.pendingRemoteIceCandidates.length === 0) return;
    const pending = this.pendingRemoteIceCandidates;
    this.pendingRemoteIceCandidates = [];
    console.log(`[ClientPeerManager] Draining ${pending.length} buffered remote ICE candidate(s)`);
    for (const candidate of pending) {
      this.connection.addIceCandidate(candidate);
    }
  }

  public send(data: string | ArrayBuffer, label?: string): boolean {
    if (!this.connection) return false;
    return this.connection.send(data, label);
  }

  public close(reason: string): void {
    this.closed = true;
    this.cancelRetry();
    this.cancelDisconnectTimer();
    this.cancelIceDisconnectGrace();
    this.cancelGatherBuffer();
    this.destroyPreWarmedConnection();
    this.destroyConnection();
    this.connected = false;
    this.remoteDescriptionSet = false;
    this.pendingRemoteIceCandidates = [];
  }

  public isConnected(): boolean {
    return this.connected;
  }

  public getRetryAttempt(): number {
    return this.retryAttempt;
  }

  public resetRetries(): void {
    this.retryAttempt = 0;
  }

  private waitForCandidatesThenForwardSdp(
    connection: IPeerConnection,
    initialSdp: RTCSessionDescriptionInit,
    isStale: () => boolean,
  ): void {
    const rtcPc = connection.getRtcPeerConnection();
    if (!rtcPc) {
      this.callbacks.onSignalGenerated(initialSdp);
      return;
    }
    this.cancelGatherBuffer();
    this.gatherBuffer = new SdpGatherBuffer(ClientPeerManager.ICE_GATHER_TIMEOUT_MS);
    this.gatherBuffer.buffer(rtcPc, initialSdp).then((fullSdp) => {
      if (isStale()) return;
      const count = (fullSdp.sdp?.match(/a=candidate:/g) || []).length;
      console.log(`[ClientPeerManager] Forwarding SDP answer, candidates baked in: ${count}`);
      this.callbacks.onSignalGenerated(fullSdp);
    });
  }

  private cancelGatherBuffer(): void {
    this.gatherBuffer?.cancel();
    this.gatherBuffer = null;
  }

  private iceDisconnectGraceTimer: ReturnType<typeof setTimeout> | null = null;

  private wireEvents(
    connection: IPeerConnection,
    optimizationConfig: { codecPreferences?: { video?: string[]; audio?: string[] }; maxBitrate?: number; targetFrameRate?: number; degradationPreference?: string; lowLatencyMode?: boolean; jitterBufferTarget?: number },
  ): void {
    const epoch = this.connectionEpoch;
    const isStale = () => epoch !== this.connectionEpoch;
    let iceEverChecking = false;
    let iceRestartAttempted = false;

    const handleConnected = () => {
      if (isStale() || this.connected) return;
      this.connected = true;
      this.retryAttempt = 0;
      this.cancelIceDisconnectGrace();

      this.optimizationManager.applyPostConnectionOptimizations(
        connection.getRtcPeerConnection(),
        optimizationConfig as any,
      );

      this.callbacks.onConnected();
    };

    const handleTransportLost = (source: string) => {
      if (this.hostCloseInProgress || isStale() || this.closed) return;
      if (!this.connected) return;
      console.warn(`[ClientPeerManager] Transport lost (${source}) — triggering disconnect`);
      Sentry.addBreadcrumb({ category: 'webrtc', message: `Transport lost: ${source}`, level: 'warning' });
      this.pendingCloseReason = 'ice_disconnected';
      this.triggerIceRecovery('ice_disconnected');
    };

    connection.on('connected', handleConnected);

    const rtcPc = connection.getRtcPeerConnection();
    if (rtcPc) {
      rtcPc.addEventListener('connectionstatechange', () => {
        if (isStale()) return;
        const state = rtcPc.connectionState;
        if (state === 'connected') {
          handleConnected();
        } else if (state === 'failed') {
          handleTransportLost('connectionState:failed');
        }
      });
    }

    connection.on('disconnected', () => {
      if (this.hostCloseInProgress || isStale()) return;
      this.connected = false;
      this.cancelDisconnectTimer();
      this.disconnectTimer = setTimeout(() => {
        this.disconnectTimer = null;
        if (isStale() || this.closed) return;
        const reason = this.pendingCloseReason ?? 'peer_disconnected';
        this.pendingCloseReason = null;
        this.callbacks.onDisconnected(reason);
        this.maybeRetry(reason);
      }, 150);
    });

    connection.on('signal', (...args: unknown[]) => {
      if (isStale()) return;
      const sdp = args[0] as RTCSessionDescriptionInit;
      this.recordSdpBreadcrumb(sdp);
      if (sdp.type === 'answer') {
        this.waitForCandidatesThenForwardSdp(connection, sdp, isStale);
      } else {
        this.callbacks.onSignalGenerated(sdp);
      }
    });

    connection.on('onicecandidates', (...args: unknown[]) => {
      const candidates = args[0] as RTCIceCandidate[];
      this.callbacks.onIceCandidatesGenerated(candidates);
    });

    connection.on('streamRemote', (...args: unknown[]) => {
      const stream = args[0] as MediaStream;
      if (stream.getVideoTracks().length > 0) {
        handleConnected();
      }
      this.callbacks.onRemoteStream(stream);
    });

    connection.on('channelData', (...args: unknown[]) => {
      const event = args[0] as { data: string | ArrayBuffer };
      if (typeof event.data === 'string' && this.tryHandleCloseMessage(event.data)) return;
      this.callbacks.onDataMessage(event.data);
    });

    connection.on('error', (...args: unknown[]) => {
      const error = args[0] as { id: string; message: string; error?: Error };
      console.error('[ClientPeerManager] Peer error:', error?.message, error?.error ?? '');
    });

    const rtc = connection.getRtcPeerConnection();
    if (rtc) {
      rtc.oniceconnectionstatechange = () => {
        if (isStale()) return;
        const iceState = rtc.iceConnectionState;
        if (iceState === 'checking') {
          iceEverChecking = true;
          this.callbacks.onIceChecking?.();
        } else if (iceState === 'connected' || iceState === 'completed') {
          iceRestartAttempted = false;
          this.cancelIceDisconnectGrace();
          this.callbacks.onIceConnected?.();
        } else if (iceState === 'disconnected') {
          this.handleIceDisconnected(connection, isStale);
        } else if (iceState === 'failed') {
          this.cancelIceDisconnectGrace();
          this.pendingCloseReason = 'ice_failed';
          this.callbacks.onIceFailed?.();
          this.handleIceFailed(connection, isStale, iceEverChecking, iceRestartAttempted);
          iceRestartAttempted = true;
        }
      };
    }
  }

  private triggerIceRecovery(reason: string): void {
    this.connected = false;
    this.cancelDisconnectTimer();
    this.cancelIceDisconnectGrace();
    this.callbacks.onDisconnected(reason);
    this.maybeRetry(reason);
  }

  private handleIceDisconnected(
    connection: IPeerConnection,
    isStale: () => boolean,
  ): void {
    if (this.hostCloseInProgress || isStale() || this.closed) return;
    if (!this.connected) return;

    console.warn('[ClientPeerManager] ICE disconnected — starting grace period');
    Sentry.addBreadcrumb({ category: 'webrtc', message: 'ICE disconnected, grace period started', level: 'warning' });

    this.cancelIceDisconnectGrace();
    this.iceDisconnectGraceTimer = setTimeout(() => {
      this.iceDisconnectGraceTimer = null;
      if (isStale() || this.closed || this.hostCloseInProgress) return;

      const rtc = connection.getRtcPeerConnection();
      const currentIce = rtc?.iceConnectionState;
      if (currentIce === 'connected' || currentIce === 'completed') return;

      console.warn('[ClientPeerManager] ICE did not recover within grace period — disconnecting');
      Sentry.addBreadcrumb({ category: 'webrtc', message: `ICE grace expired, state: ${currentIce}`, level: 'error' });
      this.pendingCloseReason = 'ice_disconnected';
      this.triggerIceRecovery('ice_disconnected');
    }, ClientPeerManager.ICE_DISCONNECT_GRACE_MS);
  }

  private cancelIceDisconnectGrace(): void {
    if (this.iceDisconnectGraceTimer) { clearTimeout(this.iceDisconnectGraceTimer); this.iceDisconnectGraceTimer = null; }
  }

  private handleIceFailed(
    connection: IPeerConnection,
    isStale: () => boolean,
    iceEverChecking: boolean,
    alreadyRestarted: boolean,
  ): void {
    if (isStale() || this.closed) return;

    const decision = IceHealthGuard.decideIceFailureAction({ iceEverChecking, alreadyRestarted });

    if (decision.action === 'instant_retry') {
      console.warn('[ClientPeerManager] Instant ICE failure (never reached checking) — fast-retrying');
      Sentry.addBreadcrumb({ category: 'webrtc', message: 'Instant ICE failure — no checking state', level: 'warning' });
      this.triggerIceRecovery('ice_instant_fail');
    } else if (decision.action === 'restart') {
      console.log('[ClientPeerManager] ICE failed — attempting ICE restart');
      Sentry.addBreadcrumb({ category: 'webrtc', message: 'Attempting ICE restart', level: 'info' });
      void connection.restartIce().catch((err) => {
        console.error('[ClientPeerManager] ICE restart failed:', err);
        if (isStale() || this.closed) return;
        this.triggerIceRecovery('ice_restart_exhausted');
      });
    } else {
      console.warn('[ClientPeerManager] ICE restart exhausted — reconnecting with fresh PC');
      Sentry.addBreadcrumb({ category: 'webrtc', message: 'ICE restart exhausted, full reconnect', level: 'warning' });
      this.triggerIceRecovery('ice_restart_exhausted');
    }
  }

  private tryHandleCloseMessage(data: string): boolean {
    try {
      const parsed = JSON.parse(data);
      if (parsed?.__peerClose && typeof parsed.reason === 'string') {
        console.log('[ClientPeerManager] Received close reason from host:', parsed.reason);
        this.pendingCloseReason = parsed.reason;
        this.handleHostInitiatedClose(parsed.reason);
        return true;
      }
    } catch {
      // Not JSON or not a close message
    }
    return false;
  }

  private handleHostInitiatedClose(reason: string): void {
    this.connected = false;
    this.cancelRetry();
    this.cancelDisconnectTimer();
    this.connectionEpoch++;
    this.hostCloseInProgress = true;
    this.destroyConnection();
    this.hostCloseInProgress = false;
    this.pendingCloseReason = null;
    this.callbacks.onDisconnected(reason);
  }

  private maybeRetry(reason: string): void {
    if (this.closed) return;
    if (!this.retryPolicy) return;
    if (this.retryAttempt >= this.retryPolicy.maxRetries) return;
    if (!this.retryPolicy.shouldRetry(reason, this.retryAttempt)) return;
    if (!this.lastHostPeerId || !this.lastIceServers || !this.lastMetadata) return;

    this.retryAttempt++;
    const delay = this.retryPolicy.retryDelayMs(this.retryAttempt);
    this.callbacks.onRetrying(this.retryAttempt, reason);

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.lastHostPeerId && this.lastIceServers && this.lastMetadata) {
        this.connect(this.lastHostPeerId, this.lastIceServers, this.lastMetadata);
      }
    }, delay);
  }

  private cancelRetry(): void {
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
  }

  private cancelDisconnectTimer(): void {
    if (this.disconnectTimer) { clearTimeout(this.disconnectTimer); this.disconnectTimer = null; }
  }

  private recordSdpBreadcrumb(sdp: RTCSessionDescriptionInit): void {
    const kinds = this.sdpInspector.getMediaKinds(sdp.sdp);
    const hasVideo = kinds.includes('video');
    Sentry.addBreadcrumb({
      category: 'webrtc',
      message: `Viewer local SDP (${sdp.type}) m-sections`,
      level: hasVideo ? 'info' : 'warning',
      data: { type: sdp.type, kinds, hostPeerId: this.lastHostPeerId },
    });
    if (!hasVideo) {
      console.warn(
        `[ClientPeerManager] Local SDP (${sdp.type}) has no m=video — expected recvonly video transceiver.`,
        kinds,
      );
    }
  }

  private isPreWarmHealthy(): boolean {
    if (!this.preWarmedConnection) return false;
    const result = IceHealthGuard.checkPreWarm(
      this.preWarmedConnection, this.preWarmCreatedAt, ClientPeerManager.PREWARM_MAX_AGE_MS,
    );
    if (!result.healthy) {
      console.warn(`[ClientPeerManager] Pre-warm unhealthy: ${result.reason}`);
    }
    return result.healthy;
  }

  private destroyConnection(): void {
    if (this.connection) { this.connection.destroy(); this.connection = null; }
  }

  private destroyPreWarmedConnection(): void {
    if (this.preWarmedConnection) { this.preWarmedConnection.destroy(); this.preWarmedConnection = null; }
  }
}
